// Walkthrough script.
//
// Each step targets a real UI element via a CSS selector — the wrapper
// elements in Sidebar/PageHeader/PageHeaderTopRow carry matching
// `data-tour-id` attributes. Steps whose target never mounts (e.g. admin-only
// sidebar items for a non-admin agent, or desktop-only header links on
// mobile) auto-skip after a short wait.
//
// Order = visible reading order of the screenshot: top header, then search
// bar, then sidebar top-to-bottom.
//
// Two marks a step can carry (VT review 7/8):
//   adminOnly  the item exists for admins only. An agent's tour leaves the step out; it used to
//              show "Not visible right now" on each of the four.
//   route      the page that holds the control. The tour goes there before it points; started
//              on a page without the search bar it used to say "Couldn't find this element".
// A step whose id starts with "sidebar-" needs the menu shown and wide; the tour sees to that.

import { PATH_SEARCH } from "../../Util/constants";

export const STEPS = [
  {
    id: "welcome",
    selector: null, // centered modal, no anchor
    placement: "center",
    title: "Welcome to VillaTracker",
    body:
      "Quick 60-second tour of the main controls. You can skip any time - we won't show this again.",
  },
  {
    id: "whats-new",
    selector: '[data-tour-id="whats-new"]',
    placement: "bottom",
    title: "What's new",
    body:
      "Product updates, new destinations and recently added villas land here. Click any time to see the latest.",
  },
  {
    id: "header-collections",
    selector: '[data-tour-id="header-collections"]',
    placement: "bottom",
    title: "Collections",
    body:
      "Curated villa sets - themed, seasonal, or hand-picked by our team. Great for inspiration when a client is undecided.",
  },
  {
    id: "header-hot-destinations",
    selector: '[data-tour-id="header-hot-destinations"]',
    placement: "bottom",
    title: "Hot Destinations",
    body:
      "What's trending right now. Useful for proactive outreach to clients before peak demand hits a region.",
  },
  {
    id: "header-wish-list",
    selector: '[data-tour-id="header-wish-list"]',
    placement: "bottom",
    title: "Wish List",
    body:
      "Properties you've saved for a specific client. Build it up over time and turn it into an offer in one click.",
  },
  {
    id: "header-favorite",
    selector: '[data-tour-id="header-favorite"]',
    placement: "bottom",
    title: "Your favorites",
    body:
      "Personal favorites across all clients - your shortcut back to villas you book often.",
  },
  {
    id: "search-destination",
    route: PATH_SEARCH,
    selector: '[data-tour-id="search-destination"]',
    placement: "bottom",
    title: "Where to?",
    body:
      "Type a city, region or country. Suggestions are powered by our active inventory, so anything you pick has real listings behind it.",
  },
  {
    id: "search-dates",
    route: PATH_SEARCH,
    selector: '[data-tour-id="search-dates"]',
    placement: "bottom",
    title: "Travel dates",
    body: "Pick arrival and departure. Pricing and availability flow from here.",
  },
  {
    id: "search-guests",
    route: PATH_SEARCH,
    selector: '[data-tour-id="search-guests"]',
    placement: "bottom",
    title: "Guests, bedrooms, bathrooms",
    body: "Size the property to the party. We won't show villas that can't fit the count.",
  },
  {
    id: "search-button",
    route: PATH_SEARCH,
    selector: '[data-tour-id="search-button"]',
    placement: "bottom",
    title: "Run a search",
    body: "Returns matching villas in seconds. Add filters next for finer control.",
  },
  {
    id: "search-filters",
    route: PATH_SEARCH,
    selector: '[data-tour-id="search-filters"]',
    placement: "bottom",
    title: "Advanced filters",
    body:
      "Price band, amenities, must-haves (pool, sea view, AC…). Use this when 'Where to' is too broad.",
  },
  {
    id: "sidebar-admin",
    adminOnly: true,
    selector: '[data-tour-id="sidebar-admin"]',
    placement: "right",
    title: "Admin",
    body:
      "Admin-only console: agencies, agents, approvals and platform-wide controls. Auto-hidden for non-admin users.",
  },
  {
    id: "sidebar-dashboard",
    selector: '[data-tour-id="sidebar-dashboard"]',
    placement: "right",
    title: "Dashboard",
    body: "Your at-a-glance: recent activity, bookings in progress, things needing attention.",
  },
  {
    id: "sidebar-favourites-admin",
    adminOnly: true,
    selector: '[data-tour-id="sidebar-favourites-admin"]',
    placement: "right",
    title: "Favourites (Admin)",
    body:
      "Admin-only view: properties marked as 'favourite' across every agent. Useful for curating featured inventory.",
  },
  {
    id: "sidebar-map",
    selector: '[data-tour-id="sidebar-map"]',
    placement: "right",
    title: "Interactive map",
    body: "Same inventory, on a world map. Pan and click clusters to drill into a region.",
  },
  {
    id: "sidebar-clients",
    selector: '[data-tour-id="sidebar-clients"]',
    placement: "right",
    title: "Your clients",
    body: "Every client you've added, with their saved searches, wish lists and offer history.",
  },
  {
    id: "sidebar-reservations",
    selector: '[data-tour-id="sidebar-reservations"]',
    placement: "right",
    title: "Reservations",
    body:
      "Active and historical bookings. Filter by status, drill into a booking to see payment + Guesty state.",
  },
  {
    id: "sidebar-reports",
    selector: '[data-tour-id="sidebar-reports"]',
    placement: "right",
    title: "Reports",
    body: "Bookings, commissions and performance over time. Export when you need to share.",
  },
  {
    id: "sidebar-profile",
    selector: '[data-tour-id="sidebar-profile"]',
    placement: "right",
    title: "Your profile",
    body: "Personal details, currency, signature on outgoing offers. Keep this current - clients see it.",
  },
  {
    id: "sidebar-touch",
    selector: '[data-tour-id="sidebar-touch"]',
    placement: "right",
    title: "Get In Touch",
    body: "Reach our team directly when something's off or you need help with a booking.",
  },
  {
    id: "sidebar-getting-started",
    selector: '[data-tour-id="sidebar-getting-started"]',
    placement: "right",
    title: "Getting Started",
    body:
      "Tutorials, demo videos and onboarding material. Come back here any time you need a refresher.",
  },
  {
    id: "sidebar-media-directory",
    selector: '[data-tour-id="sidebar-media-directory"]',
    placement: "right",
    title: "Media Directory",
    body:
      "Photos, videos and marketing assets for properties. Pull these into offers and proposals.",
  },
  {
    id: "sidebar-media-directory-admin",
    adminOnly: true,
    selector: '[data-tour-id="sidebar-media-directory-admin"]',
    placement: "right",
    title: "Media Directory (Admin)",
    body:
      "Admin-only upload / curation view for the media library. Manage what other agents can pull from.",
  },
  {
    id: "sidebar-destinations-admin",
    adminOnly: true,
    selector: '[data-tour-id="sidebar-destinations-admin"]',
    placement: "right",
    title: "Destinations (Admin)",
    body:
      "Admin view of property counts per destination - useful for spotting inventory gaps.",
  },
  {
    id: "sidebar-faqs",
    selector: '[data-tour-id="sidebar-faqs"]',
    placement: "right",
    title: "FAQs",
    body:
      "Common questions about bookings, payments, commissions and platform features. Skim it once - saves a lot of support tickets.",
  },
];

/** Does the step point at something in the left menu? */
export const needsMenu = (step) => Boolean(step && typeof step.id === "string" && step.id.startsWith("sidebar-"));

// Below this width the page shell keeps the left menu closed (pages/index.jsx), so a menu item
// can never be pointed at.
export const MENU_MIN_WIDTH = 800;

/** Can the left menu be opened on a screen this wide? */
export const menuCanOpen = (width) => !(Number(width) < MENU_MIN_WIDTH);

/**
 * The steps this user is shown on this screen.
 *  - role: everything for an admin, no admin-only item for anyone else.
 *  - menu: false on a screen where the left menu cannot open (a phone). The menu steps are then
 *    left out. Kept in, each of them waited for a menu that never came: eleven silent pauses.
 */
export const stepsFor = (role, { menu = true } = {}) => {
  const forRole = String(role || "").trim().toLowerCase() === "admin" ? STEPS : STEPS.filter((step) => !step.adminOnly);
  return menu ? forRole : forRole.filter((step) => !needsMenu(step));
};
